/* The new header search with nothing typed or nothing found: categories and best sellers instead of a dead end, fitted to the shopper's machine where it knows one. */
(function () {
  'use strict';

  if (theme.searchEmptyStates) return;

  // The theme editor's pick allows four (search_empty_categories in config/settings_schema.json); a model lists at most four.
  const CATEGORY_LIMIT = 4;
  const MACHINE_FILTER = 'filter.p.m.rpm_fitment.compatible_machines';
  const INCOMPLETE_ATTR = 'data-search-incomplete';
  // Merchandising, not search results: PredictiveSearch doesn't count their rows as search successes.
  const FALLBACK_ATTR = 'data-search-fallback';
  // Which products stand in, for PredictiveSearch's events (theme.js SEARCH_FEATURED reads FEATURED): the machine's best sellers, or the
  // theme editor's collection.
  const BEST_SELLERS = 'best-sellers';
  const FEATURED = 'featured';

  // Every lookup the empty states read, started as search opens and again at idle, at low priority. A panel reads only what
  // has arrived and never waits for the rest.
  function prefetch(search) {
    const machine = activeMachine();
    const loads = [theme.searchBestSellers.load(search.dataset.featuredUrl)];
    if (machine && machineLookups()) {
      loads.push(
        theme.searchBestSellers.load(machineUrl(search.dataset.bestSellersUrl, machine.id)),
        theme.searchModelCollections.load(search.dataset.modelCollectionsUrl),
        theme.searchMachineFits.load(search.dataset.machineFitsUrl, machine.id),
      );
    }
    Promise.all(loads).catch(() => {});
  }

  // The machine's model's collections, then the editor's picks known to hold a part for it; without a machine, the picks.
  // From what has arrived, so PredictiveSearch's zero state reads them without waiting.
  function readyCategories(search) {
    const picks = theme.searchBestSellers.readyCategories(search.dataset.featuredUrl);
    const machine = activeMachine();
    if (!machine || !machineLookups()) return { rows: (picks || []).slice(0, CATEGORY_LIMIT), complete: picks !== null };
    const entries = theme.searchModelCollections.ready(search.dataset.modelCollectionsUrl);
    const fits = theme.searchMachineFits.ready(search.dataset.machineFitsUrl, machine.id);
    const own = entries ? theme.searchModelCollections.rowsFor(entries, machine.name, []) : [];
    const handles = new Set(own.map(handleOf));
    const fitting = fits && picks ? picks.filter((row) => !handles.has(handleOf(row)) && fits.has(handleOf(row))) : [];
    return { rows: own.concat(fitting).slice(0, CATEGORY_LIMIT), complete: !!(picks && entries && fits) };
  }

  // A machine shopper's best sellers are the parts that fit it (and, signed in, that they haven't bought); without a machine, or
  // with none that fit, the collection picked in the theme editor. Never the store's best sellers.
  function readyProducts(search) {
    const machine = activeMachine();
    const fitting = machine ? theme.searchBestSellers.readyRows(machineUrl(search.dataset.bestSellersUrl, machine.id)) : [];
    if (fitting && fitting.length) return { section: productsSection(search, 'bestSellers', fitting, BEST_SELLERS), complete: true };
    const featured = theme.searchBestSellers.readyRows(search.dataset.featuredUrl);
    const section = featured && productsSection(search, 'featured', featured, FEATURED);
    return { section, complete: fitting !== null && featured !== null };
  }

  // A new visitor's zero state, which has nothing else to show, waits for its products, the machine's best sellers among them (typing
  // replaces it at any time): { section, complete }, where none may be by design (no collection picked). Incomplete ones aren't kept.
  async function fallback(search) {
    const ready = readyProducts(search);
    if (ready.complete) return ready;
    const machine = activeMachine();
    await Promise.all([
      theme.searchBestSellers.load(search.dataset.featuredUrl),
      machine ? theme.searchBestSellers.load(machineUrl(search.dataset.bestSellersUrl, machine.id)) : null,
    ]);
    return readyProducts(search);
  }

  // No matches: the message (snippets/search-results.liquid) shows at once, with whatever categories and best sellers have
  // arrived, in the columns typed results use. Anything missing marks the results incomplete, so they aren't kept.
  function prepareNone(search, none) {
    const products = readyProducts(search);
    const found = readyCategories(search);
    if (!products.complete || !found.complete) none.setAttribute(INCOMPLETE_ATTR, '');
    const first = none.querySelector('.results__group-1');
    if (first && found.rows.length) first.appendChild(categorySection(search, found.rows));
    const second = [products.section, found.rows.length ? categorySection(search, found.rows) : null].filter(Boolean);
    if (second.length) none.appendChild(theme.searchOverlayParts.element('div', 'results__group-2', second));
  }

  function productsSection(search, headingKey, rows, kind) {
    const section = search.buildProductsSection(text(search, headingKey), rows);
    if (section) section.setAttribute(FALLBACK_ATTR, kind);
    return section;
  }

  // One column's categories, from rows the other column may share.
  function categorySection(search, rows) {
    const section = search.buildSectionShell('results--collections', text(search, 'categories'));
    section.setAttribute(FALLBACK_ATTR, '');
    section.querySelector('ul').append(...rows.map((row) => row.cloneNode(true)));
    return section;
  }

  function machineUrl(url, machineId) {
    const target = new URL(url, window.location.origin);
    target.searchParams.set(MACHINE_FILTER, machineId);
    return `${target.pathname}${target.search}`;
  }

  function machineLookups() {
    return !!(theme.searchModelCollections && theme.searchMachineFits);
  }

  function handleOf(row) {
    return theme.searchOverlayParts.collectionHandle(row);
  }

  // search-fitment.js's machine in either arm of the fitment test: none while it's switched off or a shared link's is looked up.
  function activeMachine() {
    const fitment = theme.searchFitment;
    const status = fitment && fitment.machineStatus ? fitment.machineStatus() : null;
    return status && status.machine && status.machine.id ? status.machine : null;
  }

  // Strings come from data-text-* attributes Liquid translates (snippets/predictive-search.liquid).
  function text(search, key) {
    return search.dataset[`text${key[0].toUpperCase()}${key.slice(1)}`] || '';
  }

  theme.searchEmptyStates = { prefetch, categories: readyCategories, fallback, prepareNone };
})();
